import { useState, useEffect } from "react";
import { useDossierStore } from "../store/dossierStore";
import { fetchCompteDeResultatPrimitif, type CompteDeResultatPrimitif } from "../lib/compte-resultat";
import { formatMontantFCFA } from "@/lib/formatMontant";

const fmt = (n: number) => formatMontantFCFA(n);

export default function CompteDeResultat() {
  const { activeDossier } = useDossierStore();
  const [data, setData] = useState<CompteDeResultatPrimitif | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchCompteDeResultatPrimitif(activeDossier?.id ?? null)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : "Erreur"))
      .finally(() => setLoading(false));
  }, [activeDossier?.id]);

  if (loading) {
    return (
      <div style={{ fontFamily: "'Sora', sans-serif", padding: "24px" }}>
        <div style={{ textAlign: "center", color: "#9A8878", padding: "48px" }}>Chargement…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ fontFamily: "'Sora', sans-serif", padding: "24px" }}>
        <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: "12px", padding: "16px", color: "#EF4444" }}>
          ⚠ {error}
        </div>
      </div>
    );
  }

  const cr = data ?? { charges: [], produits: [], totalCharges: 0, totalProduits: 0, resultat: 0 };
  const { charges, produits, totalCharges, totalProduits, resultat } = cr;
  const hasData = charges.length > 0 || produits.length > 0;

  return (
    <div style={{ fontFamily: "'Sora', sans-serif", padding: "24px" }}>
      <div
        style={{
          background: "linear-gradient(135deg, rgba(59,130,246,0.1), rgba(96,165,250,0.05))",
          border: "1px solid rgba(59,130,246,0.2)",
          borderRadius: "16px",
          padding: "24px 32px",
          marginBottom: "24px",
        }}
      >
        <h1 style={{ fontSize: "1.5rem", fontWeight: 800, color: "#1A0A00", marginBottom: "4px" }}>
          Compte de résultat primitif
        </h1>
        <p style={{ fontSize: "0.9rem", color: "#5A4A3A", lineHeight: 1.5 }}>
          Aperçu à partir des opérations affectées aux comptes de charges (6) et produits (7).
          {activeDossier && (
            <span style={{ fontWeight: 600, marginLeft: "4px" }}>— {activeDossier.nom}</span>
          )}
        </p>
      </div>

      {!hasData ? (
        <div
          style={{
            background: "white",
            border: "1px solid #EAE0D5",
            borderRadius: "16px",
            padding: "48px 24px",
            textAlign: "center",
            color: "#9A8878",
          }}
        >
          <div style={{ fontSize: "2.5rem", marginBottom: "12px" }}>📊</div>
          <div style={{ fontWeight: 600, marginBottom: "8px" }}>Aucune donnée</div>
          <div style={{ fontSize: "0.9rem" }}>
            Affectez vos transactions aux comptes 6 et 7 pour voir apparaître le compte de résultat.
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "24px",
            alignItems: "start",
          }}
        >
          {/* Charges (classe 6) */}
          <div
            style={{
              background: "white",
              border: "1px solid #EAE0D5",
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <div
              style={{
                padding: "16px 20px",
                background: "rgba(239,68,68,0.06)",
                borderBottom: "1px solid #EAE0D5",
                fontWeight: 700,
                color: "#1A0A00",
                fontSize: "1rem",
              }}
            >
              Charges (classe 6)
            </div>
            <div style={{ padding: "16px 20px" }}>
              {charges.map((l) => (
                <div
                  key={l.numero}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    padding: "8px 0",
                    borderBottom: "1px solid #F5F0E8",
                    fontSize: "0.9rem",
                  }}
                >
                  <span style={{ color: "#5A4A3A" }}>
                    <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#1A0A00", marginRight: "8px" }}>
                      {l.numero}
                    </span>
                    {l.libelle}
                  </span>
                  <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#EF4444" }}>
                    {fmt(l.montant)}
                  </span>
                </div>
              ))}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  padding: "12px 0 0",
                  marginTop: "8px",
                  borderTop: "2px solid #EAE0D5",
                  fontWeight: 700,
                  fontSize: "1rem",
                }}
              >
                <span>Total charges</span>
                <span style={{ color: "#EF4444" }}>{fmt(totalCharges)}</span>
              </div>
            </div>
          </div>

          {/* Produits (classe 7) */}
          <div
            style={{
              background: "white",
              border: "1px solid #EAE0D5",
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <div
              style={{
                padding: "16px 20px",
                background: "rgba(34,197,94,0.06)",
                borderBottom: "1px solid #EAE0D5",
                fontWeight: 700,
                color: "#1A0A00",
                fontSize: "1rem",
              }}
            >
              Produits (classe 7)
            </div>
            <div style={{ padding: "16px 20px" }}>
              {produits.map((l) => (
                <div
                  key={l.numero}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    padding: "8px 0",
                    borderBottom: "1px solid #F5F0E8",
                    fontSize: "0.9rem",
                  }}
                >
                  <span style={{ color: "#5A4A3A" }}>
                    <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#1A0A00", marginRight: "8px" }}>
                      {l.numero}
                    </span>
                    {l.libelle}
                  </span>
                  <span style={{ fontFamily: "monospace", fontWeight: 600, color: "#22C55E" }}>
                    {fmt(l.montant)}
                  </span>
                </div>
              ))}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  padding: "12px 0 0",
                  marginTop: "8px",
                  borderTop: "2px solid #EAE0D5",
                  fontWeight: 700,
                  fontSize: "1rem",
                }}
              >
                <span>Total produits</span>
                <span style={{ color: "#22C55E" }}>{fmt(totalProduits)}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasData && (
        <div
          style={{
            marginTop: "24px",
            background: "white",
            border: "1px solid #EAE0D5",
            borderRadius: "16px",
            padding: "20px 24px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          }}
        >
          <span style={{ fontWeight: 700, fontSize: "1.1rem", color: "#1A0A00" }}>
            Résultat primitif
          </span>
          <span
            style={{
              fontFamily: "monospace",
              fontWeight: 800,
              fontSize: "1.25rem",
              color: resultat >= 0 ? "#22C55E" : "#EF4444",
            }}
          >
            {resultat >= 0 ? "+" : ""}{fmt(resultat)}
          </span>
        </div>
      )}

      {hasData && (
        <p style={{ fontSize: "0.75rem", color: "#9A8878", marginTop: "16px" }}>
          Ce compte de résultat est primitif : il reflète les affectations avant traitement comptable et clôture par l'expert.
        </p>
      )}
    </div>
  );
}
